'use client'

import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { useAuth } from '@/components/providers/auth-provider'
import { SettingsHistoryPopover } from '@/components/settings/settings-history-popover'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { calculateEndTime } from '@/lib/time'
import { useTRPC } from '@/trpc/react'
import { arktypeResolver } from '@hookform/resolvers/arktype'
import {
  attendanceSettingsSchema,
  breakSettingsSchema,
  BusinessSettings,
  SettingKey,
  SettingValueMap,
  shiftSettingsSchema,
  tardinessSettingsSchema,
} from '@repo/db/schema'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Clock, Coffee, XCircle } from 'lucide-react'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/business_settings/')({
  component: RouteComponent,
})

// Helper function to get a typed setting value
function getTypedSettingValue<K extends SettingKey>(
  settings: BusinessSettings[] | undefined,
  key: K,
): SettingValueMap[K]['infer'] | undefined {
  const settVal = settings?.find((setting) => setting.key === key)?.value as  // | SettingValueMap[K]['infer']['type']
    | SettingValueMap[K]['infer']
    | undefined
  return settVal
}

// // Helper function to get a typed setting value
// function getTypedSettingValue<K extends SettingKey>(
//   settings: BusinessSettings[] | undefined,
//   key: K,
// ): (typeof settingValidators)[K]['infer']['type'] | undefined {
//   return settings?.find((setting) => setting.key === key)?.value as
//     | (typeof settingValidators)[K]['infer']['type']
//     | undefined
// }

function RouteComponent() {
  const { user } = useAuth()
  const businessId = user?.businessId ?? ''
  const api = useTRPC()
  const queryClient = useQueryClient()

  const { data: settings, isPending: isPendingBusinessSettings } = useQuery({
    ...api.businessSettings.getBusinessSettings.queryOptions(),
  })

  // clearing a setting
  const clearSettingMutation = useMutation({
    ...api.businessSettings.clearSetting.mutationOptions({
      onSuccess: (data) => {
        toast.success(`${data.clearedSetting?.key} setting cleared successfully`)
        queryClient.invalidateQueries({
          queryKey: api.businessSettings.getBusinessSettings.queryKey(),
        })
      },
      onError: (error) => {
        console.error('Error clearing setting:', error)
        toast.error('Failed to clear setting')
      },
    }),
  })

  function handleClearSetting(key: SettingKey) {
    clearSettingMutation.mutate({ key })
  }

  // --- Shift Settings ---

  // Mutation for updating multiple shift settings at once
  const updateShiftSettingsMutation = useMutation({
    ...api.businessSettings.updateShiftSettings.mutationOptions({
      onSuccess: (data) => {
        toast.success(`${data.updatedCount} shift settings updated successfully`)
        queryClient.invalidateQueries({
          queryKey: api.businessSettings.getBusinessSettings.queryKey(),
        })
      },
      onError: (error) => {
        console.error('Error updating shift settings:', error)
        toast.error('Failed to update shift settings')
      },
    }),
  })

  const officeHoursFormDefaultValues = {
    dayShiftStartTime: getTypedSettingValue(settings, 'day_shift_start_time') ?? undefined,
    dayShiftEndTime: getTypedSettingValue(settings, 'day_shift_end_time') ?? undefined,
    nightShiftStartTime: getTypedSettingValue(settings, 'night_shift_start_time') ?? undefined,
    nightShiftEndTime: getTypedSettingValue(settings, 'night_shift_end_time') ?? undefined,
    employeeShiftHoursStartTime:
      getTypedSettingValue(settings, 'employee_shift_hours_start_time') ?? undefined,
    // employeeShiftHoursEndTime:
    //   getTypedSettingValue(settings, 'employee_shift_hours_end_time') ?? undefined,
    maxDailyRegHrs: getTypedSettingValue(settings, 'max_daily_reg_hrs') ?? undefined,
    maxDailyOtHrs: getTypedSettingValue(settings, 'max_daily_ot_hrs') ?? undefined,
    maxDailyNsHrs: getTypedSettingValue(settings, 'max_daily_ns_hrs') ?? undefined,
    maxDailyNsOtHrs: getTypedSettingValue(settings, 'max_daily_ns_ot_hrs') ?? undefined,
  }

  const shiftHoursForm = useForm<typeof shiftSettingsSchema.infer>({
    resolver: arktypeResolver(shiftSettingsSchema),
    defaultValues: officeHoursFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      shiftHoursForm.reset(officeHoursFormDefaultValues)
    }
  }, [settings])

  // --- Tardiness Settings ---
  const updateTardinessSettingsMutation = useMutation({
    ...api.businessSettings.updateTardinessSettings.mutationOptions({
      onSuccess: (data) => {
        toast.success(`${data.updatedCount} shift settings updated successfully`)
        queryClient.invalidateQueries({
          queryKey: api.businessSettings.getBusinessSettings.queryKey(),
        })
      },
      onError: (error) => {
        console.error('Error updating shift settings:', error)
        toast.error('Failed to update shift settings')
      },
    }),
  })

  const tardinessFormDefaultValues = {
    tardinessGracePeriodMinutes:
      getTypedSettingValue(settings, 'tardiness_grace_period') ?? undefined,
  }

  const tardinessForm = useForm<typeof tardinessSettingsSchema.infer>({
    resolver: arktypeResolver(tardinessSettingsSchema),
    defaultValues: tardinessFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      tardinessForm.reset(tardinessFormDefaultValues)
    }
  }, [settings])

  // --- Attendance Settings ---
  const updateAttendanceSettingsMutation = useMutation({
    ...api.businessSettings.updateAttendanceSettings.mutationOptions({
      onSuccess: (data) => {
        toast.success(`${data.updatedCount} shift settings updated successfully`)
        queryClient.invalidateQueries({
          queryKey: api.businessSettings.getBusinessSettings.queryKey(),
        })
      },
      onError: (error) => {
        console.error('Error updating shift settings:', error)
        toast.error('Failed to update shift settings')
      },
    }),
  })

  const attendanceFormDefaultValues = {
    minimumHrsForPresent: getTypedSettingValue(settings, 'min_hrs_for_present') ?? undefined,
  }

  const attendanceForm = useForm<typeof attendanceSettingsSchema.infer>({
    resolver: arktypeResolver(attendanceSettingsSchema),
    defaultValues: attendanceFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      attendanceForm.reset(attendanceFormDefaultValues)
    }
  }, [settings])

  // --- Break Settings ---

  const updateBreakSettingsMutation = useMutation({
    ...api.businessSettings.updateBreakSettings.mutationOptions({
      onSuccess: (data) => {
        toast.success(`${data.updatedCount} shift settings updated successfully`)
        queryClient.invalidateQueries({
          queryKey: api.businessSettings.getBusinessSettings.queryKey(),
        })
      },
      onError: (error) => {
        console.error('Error updating shift settings:', error)
        toast.error('Failed to update shift settings')
      },
    }),
  })

  const breakFormDefaultValues = {
    breakTimeDayMins: getTypedSettingValue(settings, 'break_time_day_mins') ?? undefined,
    breakTimeNightMins: getTypedSettingValue(settings, 'break_time_night_mins') ?? undefined,
    isPaidBreak: getTypedSettingValue(settings, 'is_paid_break') ?? undefined,
  }

  const breakForm = useForm<typeof breakSettingsSchema.infer>({
    resolver: arktypeResolver(breakSettingsSchema),
    defaultValues: breakFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      breakForm.reset(breakFormDefaultValues)
    }
  }, [settings])

  return (
    <DashboardShell>
      <DashboardHeader
        heading="Business Settings"
        text="Configure your organization's time and attendance policies (applies to all employees and clients)."
      />

      <Alert variant="default" className="bg-amber-50 border-amber-200 mb-4">
        <AlertTitle className="text-amber-800">Currently Disabled</AlertTitle>
        <AlertDescription className="text-amber-700">
          Please configure client or employee settings instead.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Bulk Shift Settings Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <CardTitle className="text-lg">Shift Settings</CardTitle>
            </div>
            <CardDescription>Update multiple shift-related settings at once</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Form {...shiftHoursForm}>
              <form
                onSubmit={shiftHoursForm.handleSubmit((values) => {
                  updateShiftSettingsMutation.mutate({
                    settings: {
                      dayShiftStartTime: values.dayShiftStartTime,
                      dayShiftEndTime: values.dayShiftEndTime,
                      nightShiftStartTime: values.nightShiftStartTime,
                      nightShiftEndTime: values.nightShiftEndTime,
                      employeeShiftHoursStartTime: values.employeeShiftHoursStartTime,
                      // employeeShiftHoursEndTime: values.employeeShiftHoursEndTime,
                      maxDailyRegHrs: values.maxDailyRegHrs,
                      maxDailyOtHrs: values.maxDailyOtHrs,
                      maxDailyNsHrs: values.maxDailyNsHrs,
                      maxDailyNsOtHrs: values.maxDailyNsOtHrs,
                    },
                    validFrom: undefined, // Use current time
                  })
                })}
                className="space-y-4"
              >
                <div>
                  <h3 className="text-sm font-semibold mb-3">Day Shift Times</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={shiftHoursForm.control}
                      name="dayShiftStartTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Time</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input placeholder="" type="time" {...field} />
                              {settings && (
                                <>
                                  <SettingsHistoryPopover
                                    settingKey="day_shift_start_time"
                                    settingDisplayName="Day Shift Start Time"
                                    allSettings={settings}
                                    entityType="business"
                                    entityId={businessId}
                                  />
                                  <ClearSettingButton
                                    handleClearSetting={() =>
                                      handleClearSetting('day_shift_start_time')
                                    }
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={shiftHoursForm.control}
                      name="dayShiftEndTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Time</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input placeholder="" type="time" {...field} />
                              {settings && (
                                <>
                                  <SettingsHistoryPopover
                                    settingKey="day_shift_end_time"
                                    settingDisplayName="Day Shift End Time"
                                    allSettings={settings}
                                    entityType="business"
                                    entityId={businessId}
                                  />
                                  <ClearSettingButton
                                    handleClearSetting={() =>
                                      handleClearSetting('day_shift_end_time')
                                    }
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold mb-3">Night Shift Times</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={shiftHoursForm.control}
                      name="nightShiftStartTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Time</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input placeholder="" type="time" {...field} />
                              {settings && (
                                <>
                                  <SettingsHistoryPopover
                                    settingKey="night_shift_start_time"
                                    settingDisplayName="Night Shift Start Time"
                                    allSettings={settings}
                                    entityType="business"
                                    entityId={businessId}
                                  />
                                  <ClearSettingButton
                                    handleClearSetting={() =>
                                      handleClearSetting('night_shift_start_time')
                                    }
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={shiftHoursForm.control}
                      name="nightShiftEndTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Time</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input placeholder="" type="time" {...field} />
                              {settings && (
                                <>
                                  <SettingsHistoryPopover
                                    settingKey="night_shift_end_time"
                                    settingDisplayName="Night Shift End Time"
                                    allSettings={settings}
                                    entityType="business"
                                    entityId={businessId}
                                  />
                                  <ClearSettingButton
                                    handleClearSetting={() =>
                                      handleClearSetting('night_shift_end_time')
                                    }
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <Separator className="my-4" />

                <h3 className="text-sm font-semibold mb-3">Employee Shift Times</h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={shiftHoursForm.control}
                    name="employeeShiftHoursStartTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Time</FormLabel>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Input placeholder="" type="time" {...field} />
                            {settings && (
                              <>
                                <SettingsHistoryPopover
                                  settingKey="employee_shift_hours_start_time"
                                  settingDisplayName="Employee Shift Hours Start Time"
                                  allSettings={settings}
                                  entityType="business"
                                  entityId={businessId}
                                />
                                <ClearSettingButton
                                  handleClearSetting={() =>
                                    handleClearSetting('employee_shift_hours_start_time')
                                  }
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormItem>
                    <FormLabel>End Time</FormLabel>
                    <FormControl>
                      <div className="flex items-center justify-between">
                        <Input
                          placeholder=""
                          readOnly
                          type="time"
                          value={calculateEndTime(
                            shiftHoursForm.watch('employeeShiftHoursStartTime') ?? '',
                            shiftHoursForm.watch('maxDailyRegHrs') ?? 0,
                          )}
                          disabled
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>

                <Separator className="my-4" />

                <h3 className="text-sm font-semibold mb-3">Max Daily Hours</h3>
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={shiftHoursForm.control}
                    name="maxDailyRegHrs"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Reg Hours</FormLabel>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Input
                              placeholder=""
                              type="number"
                              {...field}
                              onChange={(e) => {
                                // convert to number
                                field.onChange(Number(e.target.value))
                              }}
                            />
                            {settings && (
                              <>
                                <SettingsHistoryPopover
                                  settingKey="max_daily_reg_hrs"
                                  settingDisplayName="Max Daily Regular Hours"
                                  allSettings={settings}
                                  entityType="business"
                                  entityId={businessId}
                                />
                                <ClearSettingButton
                                  handleClearSetting={() =>
                                    handleClearSetting('max_daily_reg_hrs')
                                  }
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={shiftHoursForm.control}
                    name="maxDailyOtHrs"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>OT Hours</FormLabel>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Input
                              placeholder=""
                              type="number"
                              {...field}
                              onChange={(e) => field.onChange(Number(e.target.value))}
                            />
                            {settings && (
                              <>
                                <SettingsHistoryPopover
                                  settingKey="max_daily_ot_hrs"
                                  settingDisplayName="Max Daily OT Hours"
                                  allSettings={settings}
                                  entityType="business"
                                  entityId={businessId}
                                />
                                <ClearSettingButton
                                  handleClearSetting={() => handleClearSetting('max_daily_ot_hrs')}
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={shiftHoursForm.control}
                    name="maxDailyNsHrs"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>NS Hours</FormLabel>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Input
                              placeholder=""
                              type="number"
                              {...field}
                              onChange={(e) => field.onChange(Number(e.target.value))}
                            />
                            {settings && (
                              <>
                                <SettingsHistoryPopover
                                  settingKey="max_daily_ns_hrs"
                                  settingDisplayName="Max Daily NS Hours"
                                  allSettings={settings}
                                  entityType="business"
                                  entityId={businessId}
                                />
                                <ClearSettingButton
                                  handleClearSetting={() => handleClearSetting('max_daily_ns_hrs')}
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={shiftHoursForm.control}
                    name="maxDailyNsOtHrs"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>NS OT Hours</FormLabel>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Input
                              placeholder=""
                              type="number"
                              {...field}
                              onChange={(e) => field.onChange(Number(e.target.value))}
                            />
                            {settings && (
                              <>
                                <SettingsHistoryPopover
                                  settingKey="max_daily_ns_ot_hrs"
                                  settingDisplayName="Max Daily NS OT Hours"
                                  allSettings={settings}
                                  entityType="business"
                                  entityId={businessId}
                                />
                                <ClearSettingButton
                                  handleClearSetting={() =>
                                    handleClearSetting('max_daily_ns_ot_hrs')
                                  }
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={true}
                  // disabled={updateShiftSettingsMutation.isPending || isPendingBusinessSettings}
                  className="w-full"
                >
                  {updateShiftSettingsMutation.isPending ? 'Updating...' : 'Update Shift Settings'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <CardTitle className="text-lg">Weekly Shift Hours</CardTitle>
            </div>
            <CardDescription>Update weekly shift hours</CardDescription>
          </CardHeader>
          <CardContent>Configure per employee</CardContent>
        </Card> */}

        {/* --- Tardiness Settings --- */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <CardTitle className="text-lg">Tardiness Settings</CardTitle>
            </div>
            <CardDescription>
              Minimum number of minutes before an employee is considered late
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...tardinessForm}>
              <form
                onSubmit={tardinessForm.handleSubmit((values) => {
                  updateTardinessSettingsMutation.mutate({
                    settings: {
                      tardinessGracePeriodMinutes: values.tardinessGracePeriodMinutes,
                      enforceTardinessIncompleteHours: values.enforceTardinessIncompleteHours,
                    },
                    validFrom: undefined,
                  })
                })}
                className="space-y-6"
              >
                <FormField
                  control={tardinessForm.control}
                  name="tardinessGracePeriodMinutes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Grace Period (minutes)</FormLabel>
                      <FormControl>
                        <div className="flex items-center justify-between">
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                          {settings && (
                            <>
                              <SettingsHistoryPopover
                                settingKey="tardiness_grace_period"
                                settingDisplayName="Tardiness Grace Period"
                                allSettings={settings}
                                entityType="business"
                                entityId={businessId}
                              />
                              <ClearSettingButton
                                handleClearSetting={() =>
                                  handleClearSetting('tardiness_grace_period')
                                }
                              />
                            </>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  disabled={true}
                  // disabled={updateTardinessSettingsMutation.isPending || isPendingBusinessSettings}
                  className="w-full"
                >
                  {updateTardinessSettingsMutation.isPending
                    ? 'Updating...'
                    : 'Update Tardiness Settings'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Rest Days Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <CardTitle className="text-lg">Rest Days Settings</CardTitle>
            </div>
            <CardDescription>Configure rest days settings</CardDescription>
          </CardHeader>
          <CardContent>Configure per Client or Employee</CardContent>
        </Card>

        {/* Attendance Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <CardTitle className="text-lg">Attendance Settings</CardTitle>
            </div>
            <CardDescription>Configure attendance settings</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...attendanceForm}>
              <form
                onSubmit={attendanceForm.handleSubmit((values) => {
                  updateAttendanceSettingsMutation.mutate({
                    settings: {
                      minimumHrsForPresent: values.minimumHrsForPresent,
                    },
                    validFrom: undefined,
                  })
                })}
                className="space-y-6"
              >
                <FormField
                  control={attendanceForm.control}
                  name="minimumHrsForPresent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum Hours for Present</FormLabel>
                      <FormControl>
                        <div className="flex items-center justify-between">
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                          {settings && (
                            <>
                              <SettingsHistoryPopover
                                settingKey="min_hrs_for_present"
                                settingDisplayName="Minimum Hours for Present"
                                allSettings={settings}
                                entityType="business"
                                entityId={businessId}
                              />
                              <ClearSettingButton
                                handleClearSetting={() =>
                                  handleClearSetting('min_hrs_for_present')
                                }
                              />
                            </>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  disabled={true}
                  // disabled={updateTardinessSettingsMutation.isPending || isPendingBusinessSettings}
                  className="w-full"
                >
                  {updateAttendanceSettingsMutation.isPending
                    ? 'Updating...'
                    : 'Update Attendance Settings'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Break Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Coffee className="h-4 w-4" />
              <CardTitle className="text-lg">Break Settings</CardTitle>
            </div>
            <CardDescription>Configure break settings</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...breakForm}>
              <form
                onSubmit={breakForm.handleSubmit((values) => {
                  updateBreakSettingsMutation.mutate({
                    settings: {
                      breakTimeDayMins: values.breakTimeDayMins,
                      breakTimeNightMins: values.breakTimeNightMins,
                      isPaidBreak: values.isPaidBreak,
                    },
                    validFrom: undefined,
                  })
                })}
                className="space-y-6"
              >
                <FormField
                  control={breakForm.control}
                  name="breakTimeDayMins"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Break Time (day shift) (mins)</FormLabel>
                      <FormControl>
                        <div className="flex items-center justify-between">
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                          {settings && (
                            <>
                              <SettingsHistoryPopover
                                settingKey="break_time_day_mins"
                                settingDisplayName="Break Time (mins)"
                                allSettings={settings}
                                entityType="business"
                                entityId={businessId}
                              />
                              <ClearSettingButton
                                handleClearSetting={() =>
                                  handleClearSetting('break_time_day_mins')
                                }
                              />
                            </>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={breakForm.control}
                  name="breakTimeNightMins"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Break Time (day shift) (mins)</FormLabel>
                      <FormControl>
                        <div className="flex items-center justify-between">
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                          {settings && (
                            <>
                              <SettingsHistoryPopover
                                settingKey="break_time_night_mins"
                                settingDisplayName="Break Time (night shift) (mins)"
                                allSettings={settings}
                                entityType="business"
                                entityId={businessId}
                              />
                              <ClearSettingButton
                                handleClearSetting={() =>
                                  handleClearSetting('break_time_night_mins')
                                }
                              />
                            </>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={breakForm.control}
                  name="isPaidBreak"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Is Paid Break</FormLabel>
                      <FormControl>
                        <div className="flex items-center justify-between">
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                          {settings && (
                            <>
                              <SettingsHistoryPopover
                                settingKey="is_paid_break"
                                settingDisplayName="Is Paid Break"
                                allSettings={settings}
                                entityType="business"
                                entityId={businessId}
                              />
                              <ClearSettingButton
                                handleClearSetting={() => handleClearSetting('is_paid_break')}
                              />
                            </>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  disabled={true}
                  // disabled={updateBreakSettingsMutation.isPending || isPendingBusinessSettings}
                  className="w-full"
                >
                  {updateBreakSettingsMutation.isPending ? 'Updating...' : 'Update Break Settings'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  )
}

function ClearSettingButton({ handleClearSetting }: { handleClearSetting: () => void }) {
  const [open, setOpen] = React.useState(false)

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <XCircle
              className="h-5 w-5 cursor-pointer hover:text-destructive"
              onClick={() => setOpen(true)}
            />
          </TooltipTrigger>
          <TooltipContent>Clear setting</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Setting</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to clear this setting? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleClearSetting()
                setOpen(false)
              }}
            >
              Clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
